import { useEffect, useState } from 'react'
import { RowsDialog } from '@/components/structured-ingest/RowsDialog'
import type { RowsQuery } from '@/services/api/structuredIngest'
import { useTableRows } from './hooks/use-structured-ingest'
import { useRowsSelection } from './useRowsSelection'

const PAGE = 100

export interface RowsTarget {
  analysisId: string
  tableId: string
  query: RowsQuery
  title: string
  subtitle?: string | null
  /** The search query that led here, for marking the matching rows. */
  highlight?: string | null
}

export function describeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/**
 * Connected rows drilldown for anything that knows a table and a row selection
 * (a search hit on a row window, a preview pair): pages through the verbatim rows.
 */
export function StructuredRowsDialog({
  caseId,
  target,
  onClose,
}: {
  caseId: string
  target: RowsTarget | null
  onClose: () => void
}) {
  const [offset, setOffset] = useState(0)
  useEffect(() => setOffset(0), [])
  const rows = useTableRows(
    caseId,
    target?.analysisId ?? '',
    target?.tableId ?? '',
    target ? { ...target.query, offset, limit: PAGE } : null
  )
  const selection = useRowsSelection(
    caseId,
    target ? `${target.analysisId}/${target.tableId}` : null
  )
  return (
    <RowsDialog
      open={target !== null}
      title={target?.title ?? ''}
      subtitle={target?.subtitle}
      page={rows.data ?? null}
      loading={rows.isFetching}
      error={rows.error ? describeError(rows.error) : null}
      highlight={target?.highlight}
      selection={selection}
      onOpenChange={open => {
        if (!open) onClose()
      }}
      onPage={setOffset}
    />
  )
}
